import React, { useState, useEffect } from 'react';
import { AppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/client';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, arrayUnion, arrayRemove } from 'firebase/firestore';
import { DeliveryDriver, User } from '../../types';

const AdminDriversScreen: React.FC = () => {
    const { setView } = React.useContext(AppContext);
    const { currentUser } = useAuth();
    const [drivers, setDrivers] = useState<DeliveryDriver[]>([]);
    const [loading, setLoading] = useState(true);

    const [newPhone, setNewPhone] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [invalidDrivers, setInvalidDrivers] = useState<string[]>([]);

    const [freshDriverRatings, setFreshDriverRatings] = useState<Record<string, { rating: number; ratingCount: number }>>({});

    useEffect(() => {
        const fetchDetails = async () => {
            if (drivers.length === 0) return;

            const ratingsMap: Record<string, { rating: number; ratingCount: number }> = {};
            const invalids: string[] = [];
            try {
                const promises = drivers.map(async (d) => {
                    const dRef = doc(db, 'users', d.id);
                    const dSnap = await getDoc(dRef);
                    if (dSnap.exists()) {
                        const data = dSnap.data();
                        ratingsMap[d.id] = {
                            rating: data.rating || 0,
                            ratingCount: data.ratingCount || 0
                        };
                    } else {
                        invalids.push(d.id);
                    }
                });
                await Promise.all(promises);
                setFreshDriverRatings(ratingsMap);
                setInvalidDrivers(invalids);
            } catch (err) {
                console.error("Error fetching fresh driver details:", err);
            }
        };
        fetchDetails();
    }, [drivers]);

    const displayedDrivers = drivers.filter(d => !invalidDrivers.includes(d.id));
    const ghostDriversCount = invalidDrivers.length;

    useEffect(() => {
        const fetchDrivers = async () => {
            if (!currentUser?.restaurantId) return;
            try {
                const restDoc = await getDoc(doc(db, 'restaurants', currentUser.restaurantId));
                if (restDoc.exists()) {
                    setDrivers(restDoc.data()?.deliveryDrivers || []);
                }
            } catch (err) {
                console.error("Erreur de chargement des livreurs:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDrivers();
    }, [currentUser?.restaurantId]);

    const [foundDriver, setFoundDriver] = useState<User | null>(null);

    const handleSearchDriver = async () => {
        if (!newPhone.trim()) return;
        setErrorMsg('');
        setFoundDriver(null);
        setSearchLoading(true);

        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('role', '==', 'livreur'), where('phone', '==', newPhone.trim()));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                setErrorMsg('Aucun livreur trouvé avec ce numéro.');
                return;
            }

            const driverData = querySnapshot.docs[0].data() as User;
            setFoundDriver({ ...driverData, uid: querySnapshot.docs[0].id });
        } catch (err) {
            console.error(err);
            setErrorMsg('Une erreur est survenue lors de la recherche.');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleConfirmAddDriver = async () => {
        if (!foundDriver || !currentUser?.restaurantId) return;

        try {
            const newDriver: DeliveryDriver = {
                id: foundDriver.uid,
                name: foundDriver.name || 'Livreur Inconnu',
                phone: foundDriver.phone || ''
            };

            // Check if already exists
            if (drivers.find(d => d.id === newDriver.id)) {
                setErrorMsg('Ce livreur est déjà dans votre liste.');
                return;
            }

            // Add to database
            const restRef = doc(db, 'restaurants', currentUser.restaurantId);
            await updateDoc(restRef, {
                deliveryDrivers: arrayUnion(newDriver)
            });

            setDrivers(prev => [...prev, newDriver]);
            setFoundDriver(null);
            setNewPhone('');
            alert('Livreur ajouté avec succès !');
        } catch (err) {
            console.error(err);
            setErrorMsg('Une erreur est survenue lors de l\'ajout.');
        }
    };

    const handleRemoveDriver = async (driver: DeliveryDriver) => {
        if (!currentUser?.restaurantId) return;
        if (!window.confirm(`Êtes-vous sûr de vouloir retirer ${driver.name} de votre liste ?`)) return;

        try {
            const restRef = doc(db, 'restaurants', currentUser.restaurantId);
            await updateDoc(restRef, {
                deliveryDrivers: arrayRemove(driver)
            });

            setDrivers(prev => prev.filter(d => d.id !== driver.id));
        } catch (err) {
            console.error(err);
            alert('Erreur lors de la suppression.');
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen pb-20">
            {/* Header */}
            <div className="bg-white shadow-sm p-4 sticky top-0 z-10 flex items-center gap-4">
                <button
                    onClick={() => setView({ screen: 'adminDashboard' })}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                </button>
                <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter flex-1 text-center">Mes Livreurs</h1>
                <div className="w-10"></div>
            </div>

            <div className="p-4 space-y-6">

                {/* Ajouter un livreur */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <h2 className="font-bold text-gray-800 mb-4">Ajouter un livreur</h2>
                    <div className="flex gap-2">
                        <input
                            type="tel"
                            placeholder="Entrez le numéro du livreur..."
                            value={newPhone}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setNewPhone(val);
                                setFoundDriver(null);
                                setErrorMsg('');
                            }}
                            maxLength={10}
                            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                        <button
                            onClick={handleSearchDriver}
                            disabled={searchLoading || !newPhone.trim()}
                            className="bg-gray-800 text-white px-6 py-3 rounded-xl font-bold hover:bg-black disabled:opacity-50 transition-colors flex items-center justify-center min-w-[120px]"
                        >
                            {searchLoading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                "Rechercher"
                            )}
                        </button>
                    </div>
                    {errorMsg && <p className="text-red-500 text-sm mt-2">{errorMsg}</p>}

                    {/* Found Driver Card */}
                    {foundDriver && (
                        <div className="mt-8 flex justify-center">
                            <div className="w-80 aspect-square bg-white rounded-[2rem] border-2 border-gray-100 shadow-2xl overflow-hidden flex flex-col items-center justify-between p-6 relative animate-fade-in group hover:border-red-500 transition-all duration-300">
                                {/* Top right info */}
                                <div className="absolute top-5 right-5 flex flex-col items-end gap-1.5">
                                    <div className="flex items-center gap-1 text-amber-500 font-black bg-amber-50/50 backdrop-blur-sm px-2.5 py-1 rounded-xl text-xs border border-amber-100/50 shadow-sm">
                                        <span className="text-sm">⭐</span> {foundDriver.rating || 0}
                                    </div>
                                    <div className="flex items-center gap-1 text-blue-600 font-bold bg-blue-50/50 backdrop-blur-sm px-2.5 py-1 rounded-xl text-[10px] border border-blue-100/50 shadow-sm">
                                        <span>📦</span> {foundDriver.ratingCount || 0} missions
                                    </div>
                                </div>

                                {/* Center content */}
                                <div className="flex-1 flex flex-col items-center justify-center pt-8">
                                    <div className="w-20 h-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full flex items-center justify-center mb-4 shadow-inner group-hover:scale-110 transition-transform duration-300">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-300" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 text-center leading-tight tracking-tight px-2">{foundDriver.name}</h3>
                                    <p className="text-gray-400 font-bold mt-1 tracking-widest text-xs">{foundDriver.phone}</p>
                                </div>

                                {/* Bottom action */}
                                <button
                                    onClick={handleConfirmAddDriver}
                                    className="w-full bg-red-600 text-white py-4 rounded-2xl font-black text-base hover:bg-red-700 transition-all shadow-[0_10px_20px_-5px_rgba(220,38,38,0.3)] active:scale-95 flex items-center justify-center gap-2 mt-4"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Ajouter ce livreur
                                </button>
                            </div>
                        </div>
                    )}

                    {!foundDriver && !errorMsg && (
                        <p className="text-xs text-gray-500 mt-2">Le livreur doit posséder un compte Yumii actif.</p>
                    )}
                </div>

                {/* Liste des livreurs */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="font-bold text-gray-800">Livreurs enregistrés ({displayedDrivers.length})</h2>
                        {ghostDriversCount > 0 && (
                            <button
                                onClick={async () => {
                                    if (window.confirm(`Nettoyer les ${ghostDriversCount} livreurs dont le compte n'existe plus ?`)) {
                                        try {
                                            const restRef = doc(db, 'restaurants', currentUser!.restaurantId!);
                                            const toRemove = drivers.filter(d => invalidDrivers.includes(d.id));
                                            for (const d of toRemove) {
                                                await updateDoc(restRef, { deliveryDrivers: arrayRemove(d) });
                                            }
                                            setDrivers(prev => prev.filter(d => !invalidDrivers.includes(d.id)));
                                            setInvalidDrivers([]);
                                        } catch (e) { console.error(e); }
                                    }
                                }}
                                className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded-lg border border-red-100 animate-pulse"
                            >
                                ⚠️ Nettoyer ({ghostDriversCount})
                            </button>
                        )}
                    </div>
                    {loading ? (
                        <div className="text-center py-8 text-gray-400">Chargement...</div>
                    ) : displayedDrivers.length === 0 ? (
                        <div className="text-center p-8 bg-white border border-dashed border-gray-300 rounded-xl text-gray-500">
                            Aucun livreur ajouté pour le moment.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {displayedDrivers.map(driver => (
                                <div key={driver.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-bold text-xl">
                                            {driver.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900">{driver.name}</p>
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm text-gray-500">{driver.phone}</p>
                                                {(freshDriverRatings[driver.id]?.rating !== undefined) && (
                                                    <div className="flex items-center gap-1 text-amber-500 font-bold text-xs bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                                                        <span>⭐</span>
                                                        <span>{freshDriverRatings[driver.id].rating}</span>
                                                        <span className="text-gray-400 font-medium">({freshDriverRatings[driver.id].ratingCount})</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveDriver(driver)}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AdminDriversScreen;
